// Resiliencia de arranque.
//
// El arranque de la app nativa toca varios subsistemas que pueden fallar de
// formas que el happy path no contempla: sin conexión, sin Firebase, sin
// sesión, permiso denegado o un plugin de Capacitor ausente. Históricamente
// cada uno de esos fallos se parchó por separado (push sin Firebase crasheaba,
// getSession sin red dejaba la app pegada, el SW rompía el arranque offline).
//
// Este módulo centraliza las garantías para que esa clase de fallo degrade con
// gracia —la app abre igual— en vez de crashear o quedarse pegada, y para que
// el fallo quede logueado en vez de propagarse en silencio.
import { supabase } from './supabase';

// Loguea un fallo de arranque sin romper nada. Usa el Sentry global si ya está
// inicializado (index.js lo expone en window.Sentry en producción); si no, es
// no-op. El propio logging nunca debe tumbar el arranque.
export function logStartupError(label, err) {
  try {
    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
      window.Sentry.captureException(err, { tags: { startup: label } });
    }
  } catch {
    /* si hasta el logging falla, no hay nada más que hacer: tragar */
  }
}

// Ejecuta un init síncrono que devuelve una función de cleanup. Si el init lanza
// (p. ej. un plugin nativo no registrado), lo loguea y devuelve un cleanup no-op
// para que el RESTO de la secuencia de arranque siga corriendo y se registren
// sus cleanups. Así un init que revienta no arrastra a los demás.
export function safeInit(label, fn) {
  try {
    const cleanup = fn();
    return typeof cleanup === 'function' ? cleanup : () => {};
  } catch (err) {
    logStartupError(label, err);
    return () => {};
  }
}

// Lanza un init asíncrono "fire-and-forget" sin que un rechazo se convierta en
// una unhandled promise rejection (y sin que un throw síncrono escape). Cubre
// los init que devuelven una promesa, como las notificaciones locales.
export function safeInvoke(label, fn) {
  try {
    Promise.resolve(fn()).catch((err) => logStartupError(label, err));
  } catch (err) {
    logStartupError(label, err);
  }
}

// ── Sesión a prueba de arranque sin conexión ──────────────────────────────────

// Lee la sesión que Supabase persiste en localStorage (clave sb-<ref>-auth-token).
// Sirve de respaldo cuando getSession() se cuelga sin conexión (típico en el
// WebView nativo al intentar refrescar el token): así la app abre igual y, si
// había sesión guardada, el usuario sigue dentro offline en vez de quedarse
// pegado en la pantalla de carga. Defensivo: solo devuelve algo con access_token.
export function leerSesionPersistida() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const sess = parsed?.currentSession || parsed?.session || parsed;
        if (sess?.access_token && sess?.user?.id) return sess;
      }
    }
  } catch {
    /* localStorage no disponible o JSON corrupto: caemos a null */
  }
  return null;
}

// getSession() con tope de tiempo. Sin conexión puede fallar de dos formas:
// (a) colgarse sin resolver nunca (al intentar refrescar el token en el WebView)
// o (b) rechazar de inmediato con error de red. El .catch cubre (b) y el
// setTimeout cubre (a); en ambos casos caemos a la sesión persistida para que
// la app abra igual y el usuario siga dentro offline.
export function getSessionConTimeout(ms = 4000) {
  const live = supabase.auth.getSession()
    .then(({ data }) => data.session ?? null)
    .catch(() => leerSesionPersistida());
  const fallback = new Promise((resolve) =>
    setTimeout(() => resolve(leerSesionPersistida()), ms)
  );
  return Promise.race([live, fallback]);
}
